import { App, Badge, Button, Card, Descriptions, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import {
  BotPricingQuote,
  BotSubscriptionInterface,
  BotSubscriptionStatus,
  useCancelSubscriptionMutation,
} from "api";
import { dateTimeFormat, format } from "libs";
import { Put, getErrorMessage } from "components";

interface BotsBillingSubscriptionCardProps {
  subscription: BotSubscriptionInterface | null;
  currentQuote: BotPricingQuote;
}

const STATUS_TONE: Record<
  BotSubscriptionStatus,
  "success" | "warning" | "error" | "default"
> = {
  [BotSubscriptionStatus.Active]: "success",
  [BotSubscriptionStatus.Trialing]: "success",
  [BotSubscriptionStatus.PastDue]: "warning",
  [BotSubscriptionStatus.Paused]: "warning",
  [BotSubscriptionStatus.Incomplete]: "warning",
  [BotSubscriptionStatus.IncompleteExpired]: "default",
  [BotSubscriptionStatus.Canceled]: "default",
  [BotSubscriptionStatus.Unpaid]: "error",
};

const STATUS_LABEL: Record<BotSubscriptionStatus, string> = {
  [BotSubscriptionStatus.Active]: "Activ",
  [BotSubscriptionStatus.Trialing]: "Trial",
  [BotSubscriptionStatus.PastDue]: "Plata restanta",
  [BotSubscriptionStatus.Paused]: "Suspendat",
  [BotSubscriptionStatus.Incomplete]: "Incomplet",
  [BotSubscriptionStatus.IncompleteExpired]: "Expirat (incomplet)",
  [BotSubscriptionStatus.Canceled]: "Anulat",
  [BotSubscriptionStatus.Unpaid]: "Neplatit",
};

export const BotsBillingSubscriptionCard = ({
  subscription,
  currentQuote,
}: BotsBillingSubscriptionCardProps) => {
  const { notification, modal } = App.useApp();
  const [cancelSubscription, { isLoading: isCanceling }] =
    useCancelSubscriptionMutation();

  if (subscription === null) {
    return (
      <Card title="Abonament">
        <Typography.Paragraph>
          Nu exista inca un abonament activ. Acesta va fi creat automat in
          momentul in care adaugi primul bot.
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Asigura-te mai intai ca ai completat datele de facturare si ai
          adaugat o metoda de plata.
        </Typography.Paragraph>
      </Card>
    );
  }

  const onCancel = () => {
    modal.confirm({
      title: "Anuleaza abonamentul?",
      content:
        "Botii continua sa functioneze pana la finalul perioadei curente, apoi devin EXPIRED. Banii nu se returneaza. Decizia se aplica la sfarsitul perioadei.",
      okText: "Anuleaza la final perioada",
      okButtonProps: { danger: true, loading: isCanceling },
      cancelText: "Renunta",
      onOk: async () => {
        try {
          await cancelSubscription(undefined).unwrap();
          notification.success({
            message: "Abonamentul va fi anulat la finalul perioadei curente.",
          });
        } catch (e) {
          notification.error({ message: getErrorMessage(e as never) });
        }
      },
    });
  };

  const totalEur = (currentQuote.total_cents / 100).toFixed(2);
  const subtotalEur = (currentQuote.subtotal_cents / 100).toFixed(2);
  const vatEur = (currentQuote.vat_cents / 100).toFixed(2);

  return (
    <Card
      title="Abonament"
      extra={
        subscription.status !== BotSubscriptionStatus.Canceled && (
          <Button danger onClick={onCancel} loading={isCanceling}>
            Anuleaza
          </Button>
        )
      }
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div>
          <Badge
            status={STATUS_TONE[subscription.status]}
            text={
              <Typography.Text strong>
                {STATUS_LABEL[subscription.status]}
              </Typography.Text>
            }
          />
          {!subscription.is_operational && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              Boti opriti
            </Tag>
          )}
        </div>

        <Descriptions column={1} size="small" colon={false}>
          <Descriptions.Item label="Boti facturati">
            {subscription.quantity}
          </Descriptions.Item>
          <Descriptions.Item label="Tier curent">
            Tier {currentQuote.tier} ·{" "}
            {(currentQuote.unit_price_cents / 100).toFixed(2)}{" "}
            {currentQuote.currency} / bot
          </Descriptions.Item>
          <Descriptions.Item label="Subtotal lunar">
            {subtotalEur} {currentQuote.currency}
          </Descriptions.Item>
          <Descriptions.Item label={`TVA ${currentQuote.vat_percent}%`}>
            {vatEur} {currentQuote.currency}
          </Descriptions.Item>
          <Descriptions.Item label="Total lunar">
            <Typography.Text strong>
              {totalEur} {currentQuote.currency}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Perioada curenta">
            <Put
              condition={
                !!subscription.current_period_start &&
                !!subscription.current_period_end
              }
            >
              {subscription.current_period_start &&
                format(subscription.current_period_start, dateTimeFormat)}
              {" — "}
              {subscription.current_period_end &&
                format(subscription.current_period_end, dateTimeFormat)}
            </Put>
          </Descriptions.Item>
          {subscription.grace_period_ends_at && (
            <Descriptions.Item label="Grace expira">
              <Typography.Text type="warning">
                {format(subscription.grace_period_ends_at, dateTimeFormat)}
                {" ("}
                {dayjs(subscription.grace_period_ends_at).locale("ro").fromNow()}
                {")"}
              </Typography.Text>
            </Descriptions.Item>
          )}
          {subscription.canceled_at && (
            <Descriptions.Item label="Anulat la">
              {format(subscription.canceled_at, dateTimeFormat)}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Space>
    </Card>
  );
};
